import {useMutation, useQueryClient} from '@github-ui/react-query'
import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {usePageDataUrl} from '@github-ui/pull-request-page-data-tooling/use-page-data-url'
import {descriptionQueryKey, type DescriptionData} from '../page-data/loaders/use-description-data'
import {
  fetchWithErrorHandling,
  parseJSONWithBetterErrors,
  throwErrorsIfBadResponse,
} from '@github-ui/pull-request-page-data-tooling/fetch-error-handling'

/**
 * Updates the description (body) of a pull request. On success, will update Tanstack query data
 * for the pull request description and edit attribution to reflect the update.
 *
 * @example
 * ```ts
 * const {mutate} = useUpdateDescriptionMutation()
 * mutate({id: pullRequest.id, body: 'new description'})
 * ```
 */
export function useUpdateDescriptionMutation(onError?: (error: Error) => void) {
  const queryClient = useQueryClient()
  const url = usePageDataUrl(PageData.updateDescription)
  const descriptionURL = usePageDataUrl(PageData.description)
  const descriptionKey = descriptionQueryKey(descriptionURL)

  return useMutation({
    networkMode: 'always',
    mutationFn: async ({body}: {body: string}) => {
      const response = await fetchWithErrorHandling(url, {method: 'PATCH', body: {body}})

      const json = await parseJSONWithBetterErrors(response)

      throwErrorsIfBadResponse(response, json)

      return json as DescriptionData
    },
    onSuccess: result => {
      queryClient.setQueryData<DescriptionData>(descriptionKey, result)
    },
    onError: async error => {
      // A 409 means the description was updated by someone else. Refetch so the reader sees the latest version.
      if (error.cause === 409) {
        await queryClient.invalidateQueries({queryKey: descriptionKey})
      }
      onError?.(error)
    },
  })
}
